import React from 'react'

import { Descendant, Editor, Node as SlateNode, Range, Transforms } from 'slate'
import { ReactEditor } from 'slate-react'

import { Comment } from 'modules/accountActivity/domain'

export const INITIAL_COMPOSER_VALUE: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }]
  }
]

export const parseCommentTree = (commentList: Comment[]) => {
  const commentTree = []
  for (const comment of commentList) {
    if (!comment.parentCommentId) {
      comment.children = []
      commentTree.push(comment)
    } else {
      const parentComment = commentTree.find(c => c.commentId === comment.parentCommentId)
      if (parentComment?.children) {
        parentComment.children.push(comment)
      }
    }
  }
  return commentTree
}

export const initialCommentsVisible = (commentTree: Comment[]) => {
  const mostRecentReplies = commentTree[commentTree.length - 1]?.children?.length || 0
  const secondMostRecentReplies = commentTree[commentTree.length - 2]?.children?.length || 0
  if (mostRecentReplies >= 2) {
    return 1
  } else if (mostRecentReplies === 1 || secondMostRecentReplies > 0) {
    return 2
  } else {
    return 3
  }
}

const serializeNodeToString = (node: Descendant): string => {
  return SlateNode.string(node)
}

export const serializeToString = (nodes: Descendant[]): string => {
  return nodes.map(serializeNodeToString).join('\n')
}

export const withMentions = (editor: Editor) => {
  const { isInline, isVoid } = editor

  editor.isInline = element => {
    return element.type === 'mention' ? true : isInline(element)
  }

  editor.isVoid = element => {
    return element.type === 'mention' ? true : isVoid(element)
  }

  return editor
}

export const moveCursorToStart = (editor: Editor) => {
  const point = { path: [0, 0], offset: 0 }
  editor.selection = { anchor: point, focus: point }
}

export const moveCursorToEnd = (editor: Editor) => {
  Transforms.select(editor, Editor.end(editor, []))
}

export const getBoundingRectangle = (editor: Editor, range: Range) => {
  return ReactEditor.toDOMRange(editor, range).getBoundingClientRect()
}

export const getStartCoordinates = (editor: Editor) => {
  const startPoint = { path: [0, 0], offset: 0 }
  return getBoundingRectangle(editor, Editor.range(editor, startPoint))
}

export const clearText = (
  editor: Editor,
  setComposerValue: React.Dispatch<React.SetStateAction<Descendant[]>>
) => {
  setComposerValue(INITIAL_COMPOSER_VALUE)
  editor.children = INITIAL_COMPOSER_VALUE
  ReactEditor.focus(editor)
  moveCursorToStart(editor)
}

export const handleDeletion = (comments: Comment[], deletedComment: Comment) => {
  if (deletedComment.parentCommentId) {
    const hasNoSiblingReplies =
      comments.filter(c => c.parentCommentId === deletedComment.parentCommentId).length <= 1
    const isParentDeleted = comments.find(c => c.commentId === deletedComment.parentCommentId)
      ?.deleted
    return hasNoSiblingReplies && isParentDeleted
      ? comments.filter(
          c =>
            c.commentId !== deletedComment.commentId &&
            c.commentId !== deletedComment.parentCommentId
        )
      : comments.filter(c => c.commentId !== deletedComment.commentId)
  } else {
    const numReplies = comments.filter(c => c.parentCommentId === deletedComment.commentId).length
    return numReplies > 0
      ? comments.map(c =>
          c.commentId === deletedComment.commentId ? { ...deletedComment, deleted: true } : c
        )
      : comments.filter(c => c.commentId !== deletedComment.commentId)
  }
}
